import { useEffect } from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight, ArrowRight } from "lucide-react";
import PageHero from "@/components/PageHero";
import DiagnosticCta from "@/components/DiagnosticCta";
import Seo from "@/components/Seo";
import { SERVICE_PAGES, CASE_STUDIES, FOUNDER } from "@/data/revamp";
import { breadcrumbSchema } from "@/lib/schemas";

const EASE = [0.76, 0, 0.24, 1];

export default function ServiceDetailPage() {
  const { slug } = useParams();
  const svc = SERVICE_PAGES.find((s) => s.slug === slug);
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);
  if (!svc) return <Navigate to="/services" replace />;

  const cs = CASE_STUDIES.find((c) => c.slug === svc.caseStudy);

  return (
    <div className="bg-[var(--bg)]">
      <Seo
        title={`${svc.title} — Orphion Global HR`}
        description={`${svc.headline} ${svc.problem}`}
        path={`/services/${svc.slug}`}
        jsonLd={[
          breadcrumbSchema([
            { name: "Home", path: "/" },
            { name: "Services", path: "/services" },
            { name: svc.title, path: `/services/${svc.slug}` },
          ]),
        ]}
      />

      <PageHero
        overline={`Service ${svc.number} / 05 · ${svc.title}`}
        titleA={svc.headline.split(",")[0] + ","}
        titleAccent={svc.headline.split(",")[1]?.trim().split(" ").slice(0, 2).join(" ") || svc.title}
        titleB={svc.headline.split(",")[1]?.trim().split(" ").slice(2).join(" ") || ""}
      >
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.35, ease: EASE }}
          className="mt-10"
        >
          <Link to="/book-diagnostic" className="btn-pill" data-testid="service-hero-cta">
            <span>Book a diagnostic call</span>
            <ArrowUpRight className="w-4 h-4" strokeWidth={1.6} />
          </Link>
        </motion.div>
      </PageHero>

      {/* Problem / Approach */}
      <section className="py-16 md:py-24 border-b border-[var(--line-strong)]">
        <div className="max-w-[1440px] mx-auto px-6 md:px-10 grid md:grid-cols-2 gap-12">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7, ease: EASE }}
          >
            <p className="overline mb-4">The problem</p>
            <p className="text-[var(--ink-80)] leading-relaxed text-base md:text-lg">{svc.problem}</p>
          </motion.div>
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7, delay: 0.1, ease: EASE }}
          >
            <p className="overline mb-4">Our approach</p>
            <p className="text-[var(--ink-80)] leading-relaxed text-base md:text-lg">{svc.approach}</p>
          </motion.div>
        </div>
      </section>

      {/* Deliverables + timeline */}
      <section className="py-16 md:py-24 border-b border-[var(--line-strong)] bg-[var(--bg-2)]">
        <div className="max-w-[1440px] mx-auto px-6 md:px-10 grid md:grid-cols-12 gap-10">
          <div className="md:col-span-7">
            <p className="overline mb-8">What you get</p>
            <ul className="space-y-5">
              {svc.deliverables.map((d, i) => (
                <motion.li
                  key={i}
                  initial={{ opacity: 0, x: 20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: 0.07 * i, ease: EASE }}
                  className="flex gap-4 items-start text-[var(--ink-80)] leading-relaxed text-sm md:text-base"
                >
                  <span className="font-serif-accent italic text-[var(--accent)] text-lg leading-none mt-0.5">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  {d}
                </motion.li>
              ))}
            </ul>
          </div>
          <div className="md:col-span-5 space-y-8">
            <div className="border border-[var(--line-strong)] bg-[var(--bg)] p-7">
              <p className="overline mb-3">Typical timeline</p>
              <p className="text-sm text-[var(--ink-80)] leading-relaxed">{svc.timeline}</p>
            </div>
            <div className="border border-[var(--line-strong)] bg-[var(--bg)] p-7">
              <p className="overline mb-3">Who you'll work with</p>
              <p className="font-display text-xl text-[var(--ink)]">{FOUNDER.name}</p>
              <p className="text-xs uppercase tracking-[0.2em] text-[var(--ink-50)] mt-1 mb-3">
                {FOUNDER.credentials.join(" · ")}
              </p>
              <p className="text-sm text-[var(--ink-70)] leading-relaxed">
                Senior-led engagements — strategy and delivery by people who have held the seat.
              </p>
            </div>
            <Link
              to={svc.segmentLink.to}
              className="flex items-center justify-between border border-[var(--line-strong)] hover:border-[var(--accent)] hover:text-[var(--accent)] px-5 py-4 transition-colors text-sm group"
              data-testid="service-segment-link"
            >
              <span className="uppercase tracking-[0.22em] text-xs">For: {svc.segmentLink.label}</span>
              <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </Link>
          </div>
        </div>
      </section>

      {/* Related case study */}
      {cs && (
        <section className="py-16 md:py-20 border-b border-[var(--line-strong)]">
          <div className="max-w-[1440px] mx-auto px-6 md:px-10">
            <p className="overline mb-6">Related proof</p>
            <Link
              to={`/case-studies/${cs.slug}`}
              className="group block border border-[var(--line-strong)] hover:bg-[var(--bg-2)] transition-colors p-7 md:p-10"
              data-testid="service-case-study-link"
            >
              <div className="flex flex-wrap items-center gap-3 mb-4">
                <span className="text-[10px] uppercase tracking-[0.22em] text-[var(--accent)] border border-[var(--accent)]/40 px-2 py-1">
                  {cs.segment}
                </span>
                <span className="text-[10px] uppercase tracking-[0.22em] text-[var(--ink-50)] border border-[var(--line-strong)] px-2 py-1">
                  Draft — pending verification
                </span>
              </div>
              <h3 className="font-display text-2xl md:text-3xl tracking-tight text-[var(--ink)] mb-3 leading-snug max-w-3xl">
                {cs.title}
              </h3>
              <p className="text-[var(--accent)] font-serif-accent italic text-lg">{cs.metric}</p>
            </Link>
          </div>
        </section>
      )}

      <DiagnosticCta />
    </div>
  );
}
